import type {
  BannerVariant,
  TermBanner as TermBannerData,
} from "@/lib/tour/banner";

/**
 * Editor-picked colours from the "Farba označenia" select. All four are colours
 * the site already uses elsewhere, so a banner doesn't introduce a new hue:
 * the CTA green, the navigation/button orange, the discount-badge red and the
 * brand blue. Text colour follows the same pairing those places use (white on
 * blue and red; near-black on green and orange, which carry white text only at
 * larger sizes than this pill).
 */
const VARIANT_CLASSES: Record<BannerVariant, string> = {
  green: "bg-[#619F1C] text-white",
  orange: "bg-[#D76A27] text-white",
  red: "bg-[#DF0024] text-white",
  blue: "bg-[#00638A] text-white",
};

/**
 * The term banner strip. No "use client": it is purely presentational, so it
 * renders both inside the client-side term card and on the server-rendered
 * order page.
 */
export default function TermBanner({
  banner,
  className = "",
}: {
  banner: TermBannerData;
  className?: string;
}) {
  return (
    // `w-fit` + `max-w-full`: the pill hugs its text rather than stretching the
    // card's width, but a long note wraps inside the card instead of pushing it
    // wider. The clamp bounds that wrapping at two lines — the card hosting the
    // pill reserves room for exactly that, and an unbounded pill would grow over
    // the price box and into the card below. It sits on the inner span rather
    // than the pill so the clipped edge falls at the text, not at the pill's
    // padding, which would let a sliver of the third line show through.
    <div
      className={`w-fit max-w-full rounded-full px-4 py-1 text-center text-sm font-semibold leading-tight md:text-[15px] ${VARIANT_CLASSES[banner.variant]} ${className}`}
      title={banner.text}
    >
      <span className='line-clamp-2'>{banner.text}</span>
    </div>
  );
}
